import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SearchIcon } from '../../../../../../assets/vectors/search.svg';
import { useCommercialInProgressConsultations } from '../../../../../../hooks/fetchers/useCommercialInProgressConsultations/useCommercialInProgressConsultations';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import { getChipColorByMatchesCount } from '../../../../../../utils/helpers/consultation/getChipColorByMatchesCount';
import moment from 'moment';
import Chip from '../../../../../designSystem/Chip/Chip';
import Pagination from '../../../../../commons/Pagination/Pagination';
import { SearchInProgressConsultationsCOMQuery } from './DashboardIPConsultsCOMTable.types';
import {
  SEARCH_IN_PROGRESS_CONSULTATIONS_QUERY_KEY,
  searchInProgressConsultationsCOMQuery,
} from './DashboardIPConsultsCOMTable.query';
import { UseCommercialConsultationsResponse } from '../../../../../../hooks/fetchers/useCommercialInProgressConsultations/useCommercialInProgressConsultations.types';
import { DashboardIPConsultsCOMContext } from '../DashboardIPConsultsCOM.context';
import Loading from '../../../../../commons/Loading/Loading';
import Button from '../../../../../designSystem/Button/Button';
import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import { formatRemainingTime } from '../../../../../../utils/helpers/date/formatRemainingTime';
import { ButtonColor } from '../../../../../designSystem/Button/Button.types';
import { ChipColors } from '../../../../../designSystem/Chip/Chip.types';

export default () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>();
  const [searchResults, setSearchResults] = useState<UseCommercialConsultationsResponse>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const { filters, setFiltersModalOpen } = useContext(DashboardIPConsultsCOMContext);

  const {
    data: consultations,
    refetch: refetchConsultations,
    isLoading: isConsultationsLoading,
    isFetching: isConsultationsFetching,
    error: consultationsError,
  } = useCommercialInProgressConsultations({
    page: currentPage,
    perPage: itemsPerPage,
    fieldsOfActivity: filters.fieldsOfActivity.toString(),
  });

  const { mutate: searchMutate, isLoading: isSearchLoading } = useMutation<
    Awaited<UseCommercialConsultationsResponse>,
    Error,
    SearchInProgressConsultationsCOMQuery
  >((data) => searchInProgressConsultationsCOMQuery(data), {
    mutationKey: SEARCH_IN_PROGRESS_CONSULTATIONS_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: (response) => {
      setSearchResults(response);
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  useEffect(() => {
    if (searchResults) {
      searchMutate({
        search: searchValue || '',
        params: {
          page: currentPage,
          perPage: itemsPerPage,
          fieldsOfActivity: filters.fieldsOfActivity.toString(),
        },
      });
    } else {
      queryClient.cancelMutations();
      refetchConsultations();
    }
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    if (searchResults) {
      searchMutate({
        search: searchValue || '',
        params: {
          page: currentPage,
          perPage: itemsPerPage,
          fieldsOfActivity: filters.fieldsOfActivity.toString(),
        },
      });
    } else {
      queryClient.cancelMutations();
      refetchConsultations();
    }
  }, [filters.fieldsOfActivity]);

  const onChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      setCurrentPage(1);
      if (value.length === 0) {
        setSearchResults(undefined);
      } else {
        searchMutate({
          search: value,
          params: {
            page: currentPage,
            perPage: itemsPerPage,
            fieldsOfActivity: filters.fieldsOfActivity.toString(),
          },
        });
      }
    },
    [searchMutate, currentPage, itemsPerPage, filters]
  );

  const numberOfPages = useMemo(() => {
    if (searchResults) {
      // @ts-ignore
      return parseInt(searchResults.total / itemsPerPage) + 1;
    } else {
      // @ts-ignore
      return parseInt(consultations?.total / itemsPerPage) + 1;
    }
  }, [consultations, searchResults, itemsPerPage]);

  const { isLoading, error } = useMergedStatesFromFetchers({
    loadings: [isConsultationsLoading, isConsultationsFetching, isSearchLoading],
    errors: [consultationsError],
  });

  return (
    <>
      <div className="dashboard-commerciaux">
        <div className="dashboard-commerciaux__header">
          <SearchInput
            placeholder="Chercher une consultation..."
            startIcon={SearchIcon}
            onChange={onChangeSearchText}
            handleSelectedSuggestion={(suggestion) => console.log(suggestion)}
          />
          <Button color={ButtonColor.Contrast} onClick={() => setFiltersModalOpen(true)}>
            {filters.fieldsOfActivity.length > 0
              ? `Filtrer (${filters.fieldsOfActivity.length})`
              : 'Filtrer'}
          </Button>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <table className="dashboard__table dashboard-invoices__table" style={{ width: '100%' }}>
              <colgroup>
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Domaine d'activité</th>
                  {/* <th>Début</th>
                  <th>Fin</th> */}
                  <th style={{ padding: 'var(--spacing-s) 0' }}>Temps restant</th>
                  <th style={{ textAlign: 'center' }}>Devis</th>
                  <th style={{ textAlign: 'center' }}>Matchs</th>
                </tr>
              </thead>
              <tbody>
                {searchResults || consultations ? (
                  (searchResults || consultations)?.results.map((consultation, index) => {
                    const { id, title, end, devis_count, matches_count, description } = consultation;
                    const color = getChipColorByMatchesCount(matches_count);
                    return (
                      <tr
                        key={index}
                        className="dashboard-commerciaux__table-tr--clickable"
                        onClick={() => navigate(`/dashboard/consultations_in_progress/${id}`)}>
                        <td>{title}</td>
                        <td>{description}</td>
                        {/* <td>{moment(start).format('DD-MM-YYYY')}</td>
                    <td>{moment(end).format('DD-MM-YYYY')}</td> */}
                        <td style={{ paddingLeft: 4 }}>{formatRemainingTime(
                              moment(end)
                                .add(18, 'hours')
                                .toDate()
                            )}</td>
                        <td>
                          {/* Color according to the number of matches */}
                          <Chip color={ChipColors.Secondary}>{devis_count.toString()}</Chip>
                        </td>
                        <td>
                          {/* Color according to the number of matches */}
                          <Chip {...{ color }}>{matches_count.toString()}</Chip>
                        </td>
                        {/* <td>{id_step}</td> */}
                      </tr>
                    );
                  })
                ) : (
                  <div style={{ position: 'absolute', width: '100%' }}>
                    <h1
                      style={{
                        color: 'var(--gray-4)',
                        fontFamily: 'Quicksand-Medium',
                        fontWeight: 400,
                      }}>
                      Il n'y a encore aucune consultation en cours dans votre département.
                    </h1>
                  </div>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
      {!isLoading && !error && <Pagination {...{ currentPage, setCurrentPage, numberOfPages }} />}
    </>
  );
};
